/**
 * XoVideoResponsive – Vanilla Web Component (no framework dependency)
 *
 * Giải quyết vấn đề flash/nháy khi browser chọn source video responsive.
 * - Poster được server-render sẵn trong video.liquid → hiển thị ngay khi HTML về
 * - Chọn đúng src dựa trên viewport bằng JS (thay vì để browser tự evaluate <source media>)
 * - Video ẩn (opacity: 0) cho đến khi sự kiện `canplay` fire → fade-in mượt mà, poster fade-out
 * - IntersectionObserver: play khi vào viewport, pause khi ra ngoài
 *
 * Attributes (kebab-case, đọc qua getAttribute):
 *   xo-src-pc        – URL video desktop
 *   xo-src-mobile    – URL video mobile (optional)
 *   xo-loop          – "true" để loop
 *   xo-autoplay      – "true" để autoplay
 *   xo-muted         – "true" để mute (default: true)
 *   xo-controls      – "true" để hiện controls
 *   xo-preload       – "none" | "metadata" | "auto" (default: "none")
 */
class XoVideoResponsive extends HTMLElement {
  constructor() {
    super();

    /** @type {HTMLVideoElement|null} */
    this._video = null;

    /** @type {HTMLElement|null} Poster <picture> được server-render sẵn */
    this._posterEl = null;

    /** @type {MediaQueryList|null} */
    this._mediaQuery = null;

    /** @type {IntersectionObserver|null} */
    this._observer = null;

    /** @type {boolean} */
    this._isInViewport = false;

    // Bind handlers để có thể remove listener đúng reference
    this._handleBreakpointChange = this._handleBreakpointChange.bind(this);
    this._handleCanPlay = this._handleCanPlay.bind(this);
  }

  // ─── Helpers đọc attribute ────────────────────────────────────────────────

  _attr(name) {
    return this.getAttribute(name) || '';
  }

  _boolAttr(name) {
    return this.getAttribute(name) === 'true';
  }

  get _srcPc() { return this._attr('xo-src-pc'); }
  get _srcMobile() { return this._attr('xo-src-mobile'); }
  get _loop() { return this._boolAttr('xo-loop'); }
  get _autoplay() { return this._boolAttr('xo-autoplay'); }
  get _muted() {
    // Default true: muted nếu attribute không có hoặc là "true"
    const val = this.getAttribute('xo-muted');
    return val === null ? true : val === 'true';
  }
  get _controls() { return this._boolAttr('xo-controls'); }
  get _preload() { return this._attr('xo-preload') || 'none'; }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  connectedCallback() {
    // Tìm <picture class="xo-video__poster"> được server-render sẵn
    this._posterEl = this.querySelector('.xo-video__poster');

    // Setup MediaQueryList để theo dõi breakpoint mobile/desktop
    this._mediaQuery = window.matchMedia('(max-width: 767px)');
    this._mediaQuery.addEventListener('change', this._handleBreakpointChange);

    // Build và append <video> vào component
    this._video = this._buildVideo();
    this._video.addEventListener('canplay', this._handleCanPlay);
    this.appendChild(this._video);

    // Nếu không có poster → hiện video ngay, không chờ canplay
    // (canplay sẽ không fire nếu không autoplay + preload='none')
    if (!this._posterEl) {
      this._video.style.opacity = '1';
    }

    // Setup IntersectionObserver để play/pause theo viewport
    this._setupIntersectionObserver();

    // Nếu autoplay và có src, trigger play ngay
    if (this._autoplay && this._video.src) {
      this._video.play().catch(() => {
        // Autoplay bị block bởi browser policy – không cần action
      });
    }
  }

  disconnectedCallback() {
    if (this._mediaQuery) {
      this._mediaQuery.removeEventListener('change', this._handleBreakpointChange);
    }
    if (this._video) {
      this._video.removeEventListener('canplay', this._handleCanPlay);
    }
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  // ─── Private methods ──────────────────────────────────────────────────────

  /** Trả về src phù hợp với viewport hiện tại */
  _getResponsiveSrc() {
    const isMobile = this._mediaQuery
      ? this._mediaQuery.matches
      : window.innerWidth < 768;

    if (isMobile && this._srcMobile) {
      return this._srcMobile;
    }
    return this._srcPc;
  }

  /** Tạo và setup thẻ <video> bên trong component */
  _buildVideo() {
    const video = document.createElement('video');

    video.className = 'xo-video-responsive__inner';
    // Bắt đầu ẩn, chờ canplay mới fade-in
    video.style.opacity = '0';
    video.style.position = 'relative';
    video.style.zIndex = '2';
    video.style.transition = 'opacity 0.3s ease';

    // Không set video.poster → poster đã được xử lý bởi <picture> server-render
    // Set poster sẽ gây 1 request tải ảnh thừa không cần thiết

    if (this._loop) video.loop = true;
    if (this._muted) video.muted = true;
    if (this._controls) video.controls = true;
    video.playsInline = true;
    video.preload = this._preload;

    // Set src trực tiếp thay vì dùng <source media="...">
    // Điều này tránh việc browser evaluate sai media query và gây flash
    const src = this._getResponsiveSrc();
    if (src) video.src = src;

    return video;
  }

  /** Khi viewport thay đổi giữa mobile/desktop, swap source nếu cần */
  _handleBreakpointChange() {
    if (!this._video) return;

    const newSrc = this._getResponsiveSrc();
    if (newSrc && this._video.getAttribute('src') !== newSrc) {
      // Ẩn lại, swap source, rồi fade-in lại
      this._video.style.opacity = '0';
      this._video.src = newSrc;
      this._video.load();
      if (this._autoplay && this._isInViewport) {
        this._video.play().catch(() => {});
      }
    }
  }

  /** Fade-in video sau khi sẵn sàng play, đồng thời ẩn poster picture */
  _handleCanPlay() {
    if (this._video) {
      this._video.style.opacity = '1';
    }
    // Ẩn poster picture sau khi video đã sẵn sàng
    if (this._posterEl) {
      this._posterEl.style.opacity = '0';
      this._posterEl.style.pointerEvents = 'none';
    }
  }

  /** Setup IntersectionObserver để play/pause theo viewport */
  _setupIntersectionObserver() {
    this._observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        this._isInViewport = entry.isIntersecting;

        if (!this._video) return;

        if (entry.isIntersecting) {
          // Vào viewport → play nếu autoplay
          if (this._autoplay) {
            this._video.play().catch(() => {
              // Autoplay bị block bởi browser policy
            });
          }
        } else {
          // Ra ngoài viewport → pause để tiết kiệm tài nguyên
          if (!this._video.paused) {
            this._video.pause();
          }
        }
      },
      {
        // Threshold 0: trigger ngay khi bất kỳ pixel nào vào/ra viewport
        threshold: 0,
      },
    );

    this._observer.observe(this);
  }
}

// Đăng ký custom element với trình duyệt
if (!customElements.get('xo-video-responsive')) {
  customElements.define('xo-video-responsive', XoVideoResponsive);
}
